import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useGlobalSearch } from "./useGlobalSearch";
import { useRecentSearches } from "./useRecentSearches";
import GlobalSearchResultBox from "./GlobalSearchResultBox";
import RecentSearchesBox from "./RecentSearchesBox";
import GlobalSearchPill from "./GlobalSearchPill";
import { navigateSearchResult } from "./globalSearchTransformer";
import { recordCommittedSearch } from "./recordSearch";
import useUrlDialog from "../../../Hooks/useUrlDialog";
import UrlDialog from "../../../components/dialog/UrlDialog";
import { dispatchNotification } from "../../../components/Utils";
import { useGuardedNavigate } from "../../../contexts/NavigationGuardContext";
import { useRewampT } from "../../localisation";
import { inputPartSx } from "../../design/recipes/input";

// Drives both the CSS transition and the dropdown gate timer, so they cannot drift.
const EXPAND_MS = 250;

export const GlobalSearchInput = ({ onOpenChange, appearance = "inline" }) => {
  const { t, i18n } = useTranslation();
  const tRewamp = useRewampT();
  const isPill = appearance === "pill";
  // Guarded so a result pick is nullified while the non-DBT registration leave-guard is active.
  const navigate = useGuardedNavigate();
  const dispatch = useDispatch();
  const {
    open: urlDialogOpen,
    externalUrl,
    handleLinkClick,
    handleClose: handleUrlDialogClose,
  } = useUrlDialog();
  const { query, setQuery, results, loading } = useGlobalSearch();
  const {
    recentSearches,
    loading: loadingRecent,
    isLoggedIn,
    fetchRecent,
    deleteOne,
    clearAll,
  } = useRecentSearches();

  const [open, setOpen] = useState(false);
  // `open` needs a query; `focused` expands the bar on click, with no typing.
  const [focused, setFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);

  const expanded = open || focused;

  // The dropdown reads anchorEl.offsetWidth on mount, so it waits out the expand animation.
  const [expandedSettled, setExpandedSettled] = useState(false);

  useEffect(() => {
    if (!expanded) {
      setExpandedSettled(false);
      return undefined;
    }
    const id = setTimeout(() => setExpandedSettled(true), isPill ? 0 : EXPAND_MS);
    return () => clearTimeout(id);
  }, [expanded, isPill]);

  const showDropdown = open && expandedSettled;

  // Recent panel: logged-in farmer focuses the bar before a searchable query; exclusive with results.
  const queryShort = query.trim().length < 3;
  const showRecent =
    focused &&
    !open &&
    queryShort &&
    isLoggedIn &&
    expandedSettled &&
    (loadingRecent || recentSearches.length > 0);

  const flatResults = useMemo(() => {
    return results.reduce((acc, group) => [...acc, ...group.items], []);
  }, [results]);

  useEffect(() => {
    if (query.length >= 3 && !open) {
      setOpen(true);
    } else if (query.length < 3) {
      setOpen(false);
    }
  }, [query]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [flatResults]);

  // Re-fetches when the query drops below the threshold so a just-recorded search appears.
  useEffect(() => {
    if (focused && isLoggedIn && queryShort) fetchRecent();
  }, [focused, isLoggedIn, queryShort, fetchRecent]);

  useEffect(() => {
    onOpenChange?.(expanded);
  }, [expanded, onOpenChange]);

  const notifyResultUnavailable = () => {
    dispatchNotification("error", ["REWAMP_SEARCH_LINK_UNAVAILABLE"], dispatch);
  };

  // Commit points only (result pick, Enter, recent re-run), so history holds whole terms, not keystroke prefixes.
  const recordQuery = (text) => {
    recordCommittedSearch(text, i18n.language?.startsWith("hi") ? "hi" : "en");
  };

  // Shared by result click and keyboard Enter; videos go through the external-link interstitial.
  const activateResult = (item) => {
    recordQuery(query);
    navigateSearchResult(item, {
      navigate,
      openExternal: handleLinkClick,
      onUnavailable: notifyResultUnavailable,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setFocused(false);
      inputRef.current?.blur();
      return;
    }

    if (!open && query.length >= 3) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        return;
      }
    }

    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < flatResults.length) {
        setOpen(false);
        activateResult(flatResults[selectedIndex]);
      } else {
        recordQuery(query);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setFocused(false);
  };

  const handleClearAll = () => {
    setQuery("");
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Re-recording floats a re-run term back to the top of history.
  const handleRecentSelect = (text) => {
    setQuery(text);
    recordQuery(text);
    inputRef.current?.focus();
  };

  // Not recorded to history until a commit (Enter or result pick).
  const handleVoiceTranscript = (text) => {
    setQuery(text.trim());
    setFocused(true);
    inputRef.current?.focus();
  };

  const focusInput = () => inputRef.current?.focus();

  const searchIconSx = isPill
    ? inputPartSx("searchPill", "icon")
    : { color: "background.tertiaryGreen", fontSize: "20px", mr: 1 };

  const inputSx = isPill
    ? inputPartSx("searchPill", "input")
    : {
        color: "background.white",
        width: "100%",
        fontSize: "0.875rem",
        "& .MuiInputBase-input": {
          p: 0,
          "&::placeholder, &::-webkit-input-placeholder, &::-moz-placeholder, &:-ms-input-placeholder": {
            color: "neutral.placeholderOnGreen",
            WebkitTextFillColor: (theme) => theme.palette.neutral.placeholderOnGreen,
            opacity: 1,
          },
        },
      };

  const searchLabel = isPill ? tRewamp("GLOBAL_SEARCH.PLACEHOLDER_ANYTHING") : t("REWAMP_GLOBAL_SEARCH");

  const renderSearchField = (describedBy) => (
    <>
      <SearchIcon aria-hidden="true" sx={searchIconSx} />
      <InputBase
        inputRef={inputRef}
        name="global-search"
        type="text"
        autoComplete="off"
        placeholder={searchLabel}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          setFocused(true);
          if (query.length >= 3) setOpen(true);
        }}
        aria-label={searchLabel}
        sx={inputSx}
        inputProps={{
          role: "combobox",
          "aria-expanded": showDropdown,
          "aria-controls": "global-search-results",
          "aria-activedescendant": selectedIndex >= 0 ? `search-result-${selectedIndex}` : undefined,
          "aria-describedby": describedBy,
          // Third-party managers ignore autoComplete="off"; these opt them out too.
          "data-1p-ignore": true,
          "data-lpignore": "true",
        }}
      />
    </>
  );

  return (
    <>
    <ClickAwayListener onClickAway={handleClose}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          flexGrow: isPill || expanded ? 1 : 0,
          minWidth: 0,
          width: isPill || expanded ? "100%" : "auto",
          // Pinned right so the capped input keeps a fixed gap from the profile section.
          justifyContent: "flex-end",
        }}
      >
        {isPill ? (
          <GlobalSearchPill
            anchorRef={anchorRef}
            onFocusInput={focusInput}
            onTranscript={handleVoiceTranscript}
            query={query}
          >
            {renderSearchField}
          </GlobalSearchPill>
        ) : (
        <Box
          ref={anchorRef}
          onClick={focusInput}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "onGreen.searchBg",
            border: "1px solid",
            borderColor: expanded ? "accent.butterscotch" : "onGreen.border",
            borderRadius: 2,
            height: "38px",
            width: expanded ? "100%" : { xs: "100%", sm: "200px", md: "250px" },
            maxWidth: expanded ? { xs: "100%", sm: 480, md: 600 } : "none",
            px: 1.5,
            // Width is excluded on collapse: animating it against the wrapper reverting to auto jitters.
            transition: expanded
              ? `width ${EXPAND_MS}ms ease, max-width ${EXPAND_MS}ms ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease`
              : "background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
            "&:hover": {
              bgcolor: "onGreen.searchBgHover",
              borderColor: "onGreen.borderStrong",
            },
            "&:focus-within": {
              borderColor: "accent.butterscotch",
              boxShadow: (theme) => theme.palette.onGreen.focusRing,
            },
          }}
        >
          {renderSearchField()}
        </Box>
        )}

        <GlobalSearchResultBox
          open={showDropdown}
          anchorEl={anchorRef.current}
          results={results}
          flatResults={flatResults}
          loading={loading}
          onClose={handleClose}
          onClearAll={handleClearAll}
          onActivate={activateResult}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />

        <RecentSearchesBox
          open={showRecent}
          anchorEl={anchorRef.current}
          recentSearches={recentSearches}
          loading={loadingRecent}
          onSelect={handleRecentSelect}
          onDeleteOne={deleteOne}
          onClearAll={clearAll}
        />
      </Box>
    </ClickAwayListener>

      {/* Video-specific disclaimer instead of UrlDialog's generic default. */}
      <UrlDialog
        open={urlDialogOpen}
        externalUrl={externalUrl}
        handleClose={handleUrlDialogClose}
        content="REWAMP_SEARCH_VIDEO_DIALOG"
        t={t}
      />
    </>
  );
};

GlobalSearchInput.propTypes = {
  onOpenChange: PropTypes.func,
  appearance: PropTypes.oneOf(["inline", "pill"]),
};

export default GlobalSearchInput;
